import { motion, useReducedMotion } from "framer-motion";
import { NX_CONTRACT, NX_SCAN } from "../../data/site";
import { useLanguage } from "../../i18n/LanguageContext";
import { ContractPanel } from "./ContractPanel";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";

const LIME = "#B8FF18";
const BLUE = "#147DFF";

const SPOKES = [
    { x: 96, y: 96, c: LIME },
    { x: 464, y: 88, c: BLUE },
    { x: 496, y: 224, c: LIME },
    { x: 448, y: 356, c: BLUE },
    { x: 112, y: 352, c: BLUE },
    { x: 64, y: 224, c: LIME },
    { x: 280, y: 56, c: BLUE },
    { x: 280, y: 392, c: LIME },
];

const ChainVisual = () => {
    const reduce = useReducedMotion();
    return (
        <svg viewBox="0 0 560 440" className="w-full" role="img" aria-label="Nexora smart contract on BNB Smart Chain">
            <circle cx="280" cy="220" r="118" fill="none" stroke={BLUE} strokeOpacity="0.18" strokeDasharray="3 7" />
            <circle cx="280" cy="220" r="150" fill="none" stroke="rgba(255,255,255,0.05)" />

            <g stroke="rgba(255,255,255,0.09)" strokeWidth="1">
                {SPOKES.map((s, i) => (
                    <line key={i} x1="280" y1="220" x2={s.x} y2={s.y} />
                ))}
            </g>

            {!reduce &&
                SPOKES.slice(0, 5).map((s, i) => (
                    <circle key={`p${i}`} r="2.4" fill={s.c}>
                        <animateMotion dur={`${5.5 + i * 1.2}s`} begin={`${i * 1.1}s`} repeatCount="indefinite" path={`M ${s.x} ${s.y} L 280 220`} />
                        <animate attributeName="opacity" values="0;0.85;0.85;0" keyTimes="0;0.12;0.85;1" dur={`${5.5 + i * 1.2}s`} begin={`${i * 1.1}s`} repeatCount="indefinite" />
                    </circle>
                ))}

            {SPOKES.map((s, i) => (
                <g key={`n${i}`}>
                    <motion.circle
                        cx={s.x}
                        cy={s.y}
                        r="8"
                        fill={s.c}
                        opacity="0.1"
                        animate={reduce ? undefined : { opacity: [0.04, 0.16, 0.04], scale: [1, 1.6, 1] }}
                        transition={{ duration: 3.2, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
                        style={{ transformOrigin: `${s.x}px ${s.y}px` }}
                    />
                    <circle cx={s.x} cy={s.y} r="3.2" fill={s.c} />
                </g>
            ))}

            <rect x="236" y="176" width="88" height="88" rx="20" fill="#111617" stroke="rgba(255,255,255,0.12)" />
            <rect x="236" y="176" width="88" height="88" rx="20" fill="none" stroke={LIME} strokeOpacity="0.15" />
            <g fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M 263 204 l -11 16 l 11 16" stroke={LIME} />
                <path d="M 297 204 l 11 16 l -11 16" stroke={LIME} />
                <path d="M 273 234 L 287 206" stroke={BLUE} />
            </g>

            <text x="280" y="426" textAnchor="middle" fontFamily="Manrope, sans-serif" fontSize="10" fontWeight="700" letterSpacing="3" fill="#626A68">
                BNB SMART CHAIN — BEP20
            </text>
        </svg>
    );
};

export const SmartContract = () => {
    const { t } = useLanguage();
    return (
        <section data-testid="smart-contract-section" className="relative overflow-hidden border-t border-white/5 bg-nx-base py-20 sm:py-24 lg:py-32">
            <div className="pointer-events-none absolute -left-32 top-1/4 h-80 w-80 rounded-full bg-nx-blue/[0.05] blur-[110px] rtl:-right-32 rtl:left-auto" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                <SectionHeader
                    index="04"
                    label={t("On-Chain Infrastructure")}
                    testId="smart-contract"
                    title={<>{t("Built on")}<br />{t("Smart Contract Technology.")}</>}
                    description={t("Explore the Nexora Global smart contract directly on BNB Smart Chain through BscScan.")}
                />

                <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
                    <div className="lg:col-span-6">
                        <Reveal>
                            <h3 className="text-xl sm:text-2xl font-extrabold tracking-tight text-nx-text" data-testid="smart-contract-heading">
                                NEXORA GLOBAL<br />SMART CONTRACT
                            </h3>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {["On-Chain Transparency", "Public Blockchain Data"].map((chip) => (
                                    <span key={chip} className="rounded-full border border-white/10 bg-nx-elev px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-nx-sub">
                                        {t(chip)}
                                    </span>
                                ))}
                            </div>
                        </Reveal>
                        <Reveal delay={0.12} className="mt-7">
                            <ContractPanel
                                label="Nexora Global Smart Contract"
                                address={NX_CONTRACT}
                                scanUrl={NX_SCAN}
                                scanLabel="View on BscScan"
                                testId="nx-contract"
                            />
                        </Reveal>
                    </div>

                    <div className="lg:col-span-6">
                        <Reveal delay={0.18} className="h-full">
                            <div className="relative flex h-full min-h-[380px] items-center justify-center overflow-hidden rounded-2xl border border-white/5 bg-nx-alt p-6" data-testid="smart-contract-visual">
                                <div className="bg-grid-fine absolute inset-0 opacity-50" aria-hidden="true" />
                                <ChainVisual />
                                <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_60px_rgba(6,8,9,0.7)]" aria-hidden="true" />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};
